// src/components/MachineStatistics/MachineStatisticsPage.js
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Navbar/Navbar";
import api from "../Axios/axios";
import "./MachineStatisticsPage2.scss";
import Select from "react-select";

const MIN_ANALYSIS_YEAR = 2025;

const ayIsimleri = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// --- Helper Fonksiyonlar ---
const toDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};
const startOfMonth = (year, monthIndex) => new Date(year, monthIndex, 1);
const endOfMonth = (year, monthIndex) =>
    new Date(year, monthIndex + 1, 0, 23, 59, 59);

const rentalOverlapsMonth = (rental, year, monthIndex) => {
    const start = toDate(rental.startDate || rental.rentalStartDate);
    const end = toDate(rental.endDate || rental.rentalEndDate) || new Date();
    if (!start) return false;
    const mStart = startOfMonth(year, monthIndex);
    const mEnd = endOfMonth(year, monthIndex);
    return start <= mEnd && end >= mStart;
};

const getRentalCraneId = (r) =>
    r.towerCraneId || r.towerCraneID || r.towerCraneIdFk || null;

const getRentalCustomerId = (r) => r._foundCompany?.id || null;

const multiSelectStyles = {
    control: (provided) => ({
        ...provided,
        borderColor: "#111827",
        minHeight: "38px",
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 5,
    }),
};

// --- Ana Bileşen ---
const MachineStatisticsPage = () => {
    const [rentals, setRentals] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [towerCranes, setTowerCranes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const now = new Date();
    const nowYear = now.getFullYear();
    const initialYear = Math.max(nowYear, MIN_ANALYSIS_YEAR);

    const [selectedYear, setSelectedYear] = useState(initialYear);
    const [selectedOwnerIds, setSelectedOwnerIds] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedModels, setSelectedModels] = useState([]);
    const [selectedTonnages, setSelectedTonnages] = useState([]);
    const [customerTypeToggle, setCustomerTypeToggle] = useState("contractor");

    // YENİ: Yazdırma Fonksiyonu
    const handlePrint = () => {
        window.print();
    };

    // -------- DATA FETCH --------
    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            setError("");
            try {
                const [rentalRes, companyRes, craneRes] = await Promise.all([
                    api.get("/Rental"),
                    api.get("/Company"),
                    api.get("/TowerCrane"),
                ]);
                setRentals(rentalRes?.data?.data ?? rentalRes?.data ?? []);
                setCompanies(companyRes?.data?.data ?? companyRes?.data ?? []);
                setTowerCranes(craneRes?.data?.data ?? craneRes?.data ?? []);
            } catch (err) {
                console.error(err);
                setError("İstatistik verileri çekilirken bir hata oluştu.");
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    // --- FİLTRE SEÇENEKLERİ (MEMO) ---
    const ownerOptions = useMemo(
        () =>
            companies
                .filter((c) => c.hasTowerCrane)
                .map((c) => ({ value: c.id, label: c.name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [companies]
    );

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        const maxYear = current + 1;
        const years = [];
        for (let y = maxYear; y >= MIN_ANALYSIS_YEAR; y--) years.push(y);
        return years;
    }, []);

    const brandOptions = useMemo(
        () =>
            [
                ...new Set(towerCranes.map((c) => c.brand || null).filter(Boolean)),
            ].map((brand) => ({ value: brand, label: brand })),
        [towerCranes]
    );

    const modelOptions = useMemo(
        () =>
            [
                ...new Set(towerCranes.map((c) => c.model || null).filter(Boolean)),
            ].map((model) => ({ value: model, label: model })),
        [towerCranes]
    );

    const tonnageOptions = useMemo(
        () =>
            [
                ...new Set(
                    towerCranes.map((c) => c.maxTonnage || null).filter(Boolean)
                ),
            ]
                .sort((a, b) => a - b)
                .map((cap) => ({ value: cap, label: `${cap} Ton` })),
        [towerCranes]
    );

    // --- YARDIMCI MAP'LER (MEMO) ---
    const craneMap = useMemo(() => {
        const map = new Map();
        towerCranes.forEach((c) => {
            if (c && c.id != null) map.set(String(c.id), c);
        });
        return map;
    }, [towerCranes]);

    const companyNameMap = useMemo(() => {
        const map = new Map();
        companies.forEach((c) => {
            if (c && c.name) map.set(c.name, c);
        });
        return map;
    }, [companies]);

    const yearNum = Number(selectedYear);

    // Kiralamaları firma bilgisiyle zenginleştir
    const rentalsWithCompanyData = useMemo(() => {
        return rentals.map(r => {
            const companyName = r.rentingCompany;
            const foundCompany = companyNameMap.get(companyName);
            return { ...r, _foundCompany: foundCompany || null };
        });
    }, [rentals, companyNameMap]);

    // -------- FİLTRELİ KİRALAMA LİSTESİ --------
    const filteredRentals = useMemo(() => {
        const yearStart = new Date(yearNum, 0, 1, 0, 0, 0);
        const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59);

        const ownerIdSet = new Set(selectedOwnerIds.map((o) => String(o.value)));
        const brandSet = new Set(selectedBrands.map((b) => b.value));
        const modelSet = new Set(selectedModels.map((m) => m.value));
        const tonnageSet = new Set(selectedTonnages.map((t) => t.value));

        return rentalsWithCompanyData.filter((r) => {
            const start = toDate(r.startDate || r.rentalStartDate);
            const end = toDate(r.endDate || r.rentalEndDate) || new Date();
            if (!start) return false;

            const overlapsYear = !(start > yearEnd || end < yearStart);
            if (!overlapsYear) return false;

            const craneId = getRentalCraneId(r);
            const crane = craneId != null ? craneMap.get(String(craneId)) : null;
            if (!crane) return false;

            if (ownerIdSet.size > 0 && !ownerIdSet.has(String(crane.ownerId)))
                return false;
            if (brandSet.size > 0 && !brandSet.has(crane.brand)) return false;
            if (modelSet.size > 0 && !modelSet.has(crane.model)) return false;
            if (tonnageSet.size > 0 && !tonnageSet.has(crane.maxTonnage))
                return false;

            return true;
        });
    }, [
        rentalsWithCompanyData,
        yearNum,
        selectedOwnerIds,
        selectedBrands,
        selectedModels,
        selectedTonnages,
        craneMap,
    ]);

    // -------- AYLIK BAŞLANGIÇ / BİTİŞ SAYILARI --------
    const { monthlyStarts, monthlyEnds } = useMemo(() => {
        const starts = Array(12).fill(0);
        const ends = Array(12).fill(0);
        filteredRentals.forEach((r) => {
            const s = toDate(r.startDate || r.rentalStartDate);
            const e = toDate(r.endDate || r.rentalEndDate);
            if (s && s.getFullYear() === yearNum) starts[s.getMonth()] += 1;
            if (e && e.getFullYear() === yearNum) ends[e.getMonth()] += 1;
        });
        return { monthlyStarts: starts, monthlyEnds: ends };
    }, [filteredRentals, yearNum]);

    // -------- FİLTRELERE UYAN TÜM MAKİNELER --------
    const cranesForFilters = useMemo(() => {
        const ownerIdSet = new Set(selectedOwnerIds.map((o) => String(o.value)));
        const brandSet = new Set(selectedBrands.map((b) => b.value));
        const modelSet = new Set(selectedModels.map((m) => m.value));
        const tonnageSet = new Set(selectedTonnages.map((t) => t.value));

        return towerCranes.filter((crane) => {
            if (!crane || crane.id == null) return false;
            if (ownerIdSet.size > 0 && !ownerIdSet.has(String(crane.ownerId)))
                return false;
            if (brandSet.size > 0 && !brandSet.has(crane.brand)) return false;
            if (modelSet.size > 0 && !modelSet.has(crane.model)) return false;
            if (tonnageSet.size > 0 && !tonnageSet.has(crane.maxTonnage))
                return false;
            return true;
        });
    }, [
        towerCranes,
        selectedOwnerIds,
        selectedBrands,
        selectedModels,
        selectedTonnages,
    ]);

    // -------- AYLIK AKTİF MAKİNE SAYISI --------
    const monthlyActiveMachines = useMemo(() => {
        const result = Array(12).fill(0);
        for (let m = 0; m < 12; m++) {
            const set = new Set();
            filteredRentals.forEach((r) => {
                if (rentalOverlapsMonth(r, yearNum, m)) {
                    const craneId = getRentalCraneId(r);
                    if (craneId != null) set.add(craneId);
                }
            });
            result[m] = set.size;
        }
        return result;
    }, [filteredRentals, yearNum]);

    // -------- AYLIK MEVCUT MAKİNE SAYISI (Creation Date) --------
    const monthlyAvailableMachines = useMemo(() => {
        const result = Array(12).fill(0);
        for (let m = 0; m < 12; m++) {
            const monthEnd = endOfMonth(yearNum, m);
            let count = 0;
            for (const crane of cranesForFilters) {
                const creation = toDate(crane.creationDate);
                if (!creation) {
                    count++;
                } else if (creation <= monthEnd) {
                    count++;
                }
            }
            result[m] = count;
        }
        return result;
    }, [cranesForFilters, yearNum]);

    // -------- MAKİNE BAZLI ÖZET + BOŞ GÜN --------
    const machineSummaries = useMemo(() => {
        const yearStart = new Date(yearNum, 0, 1, 0, 0, 0);
        const yearEnd = new Date(yearNum, 11, 31, 23, 59, 59);
        const today = new Date();
        const todayYear = today.getFullYear();

        const rentalsByCrane = new Map();
        filteredRentals.forEach((r) => {
            const craneId = getRentalCraneId(r);
            if (!craneId) return;
            const start = toDate(r.startDate || r.rentalStartDate);
            const end = toDate(r.endDate || r.rentalEndDate) || new Date();
            if (!start) return;
            const key = String(craneId);
            let arr = rentalsByCrane.get(key);
            if (!arr) { arr = []; rentalsByCrane.set(key, arr); }
            arr.push({ start, end });
        });

        const summaries = [];
        cranesForFilters.forEach((crane) => {
            const craneCreation = toDate(crane.creationDate);
            if (craneCreation && craneCreation.getFullYear() > yearNum) {
                return;
            }

            const craneIdStr = String(crane.id);
            const rentalsForCrane = rentalsByCrane.get(craneIdStr) || [];
            const yearRentals = rentalsForCrane;
            let totalDaysYear = 0, firstStart = null, lastEnd = null;

            yearRentals.forEach((r) => {
                const effStart = r.start < yearStart ? yearStart : r.start;
                const effEnd = r.end > yearEnd ? yearEnd : r.end;
                if (effEnd > effStart) {
                    totalDaysYear += Math.max(0, Math.round((effEnd.getTime() - effStart.getTime()) / MS_PER_DAY));
                }
                if (!firstStart || r.start < firstStart) firstStart = r.start;
                if (!lastEnd || r.end > lastEnd) lastEnd = r.end;
            });

            let activeStart = yearStart;
            if (craneCreation && craneCreation.getFullYear() === yearNum && craneCreation > yearStart) {
                activeStart = craneCreation;
            }
            let observeEnd = yearEnd;
            if (yearNum === todayYear) {
                observeEnd = today < yearEnd ? today : yearEnd;
            } else if (yearNum > todayYear) {
                observeEnd = activeStart;
            }

            let emptyDays = 0;
            if (observeEnd > activeStart) {
                const observedTotalDays = Math.max(0, Math.round((observeEnd.getTime() - activeStart.getTime()) / MS_PER_DAY));
                let observedBusyDays = 0;
                yearRentals.forEach((r) => {
                    const s = r.start, e = r.end;
                    const effStart = s < activeStart ? activeStart : s;
                    const effEnd = e > observeEnd ? observeEnd : e;
                    if (effEnd > effStart) {
                        observedBusyDays += Math.max(0, Math.round((effEnd.getTime() - effStart.getTime()) / MS_PER_DAY));
                    }
                });
                emptyDays = Math.max(0, observedTotalDays - observedBusyDays);
            }

            summaries.push({
                towerCraneId: crane.id,
                serialNo: crane.serialNo || crane.code || "",
                rentalCount: yearRentals.length,
                totalDays: totalDaysYear,
                emptyDays,
                firstStart,
                lastEnd,
            });
        });

        summaries.sort((a, b) => {
            if (b.rentalCount !== a.rentalCount) return b.rentalCount - a.rentalCount;
            return String(a.serialNo || "").localeCompare(String(b.serialNo || ""));
        });

        return summaries;
    }, [filteredRentals, yearNum, cranesForFilters]);

    // -------- ÖZET KARTLAR --------
    const totalMachines = useMemo(() => {
        if (monthlyAvailableMachines && monthlyAvailableMachines.length === 12) {
            return monthlyAvailableMachines[11];
        }
        return 0;
    }, [monthlyAvailableMachines]);

    const totalRentals = filteredRentals.length;
    const totalEmptyDays = useMemo(
        () => machineSummaries.reduce((sum, m) => sum + m.emptyDays, 0),
        [machineSummaries]
    );
    const totalEmptyMonthsApprox = useMemo(
        () => Math.round(totalEmptyDays / 30),
        [totalEmptyDays]
    );

    const uniqueCustomerCount = useMemo(() => {
        const customerSet = new Set();
        filteredRentals.forEach((r) => {
            const customerId = getRentalCustomerId(r);
            if (customerId != null) {
                customerSet.add(customerId);
            }
        });
        return customerSet.size;
    }, [filteredRentals]);

    // -------- MÜŞTERİ BAZLI ÖZET --------
    const allCustomerSummaries = useMemo(() => {
        const stats = new Map();
        filteredRentals.forEach((r) => {
            const company = r._foundCompany;
            if (company == null || company.id == null) return;

            const key = String(company.id);
            let stat = stats.get(key);
            if (!stat) {
                stat = {
                    id: company.id,
                    name: company.name,
                    rentalCount: 0,
                    machineSet: new Set(),
                    isRentingCustomer: company.rentTowerCrane === true,
                    isOwnerCustomer: company.hasTowerCrane === true,
                };
                stats.set(key, stat);
            }

            stat.rentalCount += 1;
            const craneId = getRentalCraneId(r);
            if (craneId != null) stat.machineSet.add(craneId);
        });

        return Array.from(stats.values()).map(stat => ({
            ...stat,
            uniqueMachineCount: stat.machineSet.size,
        }));

    }, [filteredRentals]);

    const filteredCustomerSummaries = useMemo(() => {
        const list = allCustomerSummaries.filter(c => {
            if (customerTypeToggle === 'contractor') {
                return c.isRentingCustomer;
            }
            if (customerTypeToggle === 'investor') {
                return c.isOwnerCustomer;
            }
            return false;
        });
        return list.sort((a, b) => b.rentalCount - a.rentalCount);
    }, [allCustomerSummaries, customerTypeToggle]);

    const formatDate = (date) => {
        if (!date) return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return "-";
        return d.toLocaleDateString("tr-TR");
    };

    const getFilterSubtitle = () => {
        const owners = selectedOwnerIds.map(o => o.label).join(', ');
        const brands = selectedBrands.map(b => b.label).join(', ');
        const models = selectedModels.map(m => m.label).join(', ');
        let text = owners || "Tüm Sahipler";
        if (brands) text += ` / ${brands}`;
        if (models) text += ` / ${models}`;
        return text;
    };

    // -------- RENDER --------
    return (
        <>
            {/* Yazdırma sırasında gizlenecek olan Navbar kapsayıcısı */}
            <div className="no-print-nav">
                <Navbar />
            </div>

            <div className="machine-stats-page">
                <div className="machine-stats-page__content">
                    <div className="machine-stats-page__header">
                        <div className="machine-stats-page__header-left">
                            <h1 className="machine-stats-page__title">Kule Vinç İstatistikleri</h1>
                            <div className="machine-stats-page__subtitle">
                                {getFilterSubtitle()} – {selectedYear}
                            </div>
                        </div>

                        {/* YENİ: YAZDIR BUTONU (AccountPage tarzı) */}
                        <div className="machine-stats-page__header-right">
                            <button onClick={handlePrint} className="machine-stats-page__btn">
                                <svg
                                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                >
                                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                                    <rect x="6" y="14" width="12" height="8"></rect>
                                </svg>
                                Rapor Yazdır
                            </button>
                        </div>
                    </div>

                    {/* FİLTRELER */}
                    <div className="machine-stats-page__filters">
                        <div className="machine-stats-page__filter">
                            <label>Yıl</label>
                            <select
                                className="machine-stats-page__filter-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                            >
                                {yearOptions.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="machine-stats-page__filter machine-stats-page__filter--multi">
                            <label>Makine Sahibi</label>
                            <Select
                                isMulti options={ownerOptions} value={selectedOwnerIds}
                                onChange={setSelectedOwnerIds} placeholder="Tüm Sahipler..."
                                styles={multiSelectStyles}
                            />
                        </div>
                        <div className="machine-stats-page__filter machine-stats-page__filter--multi">
                            <label>Makine Markası</label>
                            <Select
                                isMulti options={brandOptions} value={selectedBrands}
                                onChange={setSelectedBrands} placeholder="Tüm Markalar..."
                                styles={multiSelectStyles}
                            />
                        </div>
                        <div className="machine-stats-page__filter machine-stats-page__filter--multi">
                            <label>Makine Modeli</label>
                            <Select
                                isMulti options={modelOptions} value={selectedModels}
                                onChange={setSelectedModels} placeholder="Tüm Modeller..."
                                styles={multiSelectStyles}
                            />
                        </div>
                        <div className="machine-stats-page__filter machine-stats-page__filter--multi">
                            <label>Tonaj (Kapasite)</label>
                            <Select
                                isMulti options={tonnageOptions} value={selectedTonnages}
                                onChange={setSelectedTonnages} placeholder="Tüm Tonajlar..."
                                styles={multiSelectStyles}
                            />
                        </div>
                    </div>

                    {error && <div className="machine-stats-page__error">{error}</div>}
                    {loading && (
                        <div className="machine-stats-page__info">Veriler yükleniyor...</div>
                    )}

                    {/* Özet Kartlar */}
                    <div className="machine-stats-page__cards">
                        <div className="machine-stats-page__card">
                            <div className="machine-stats-page__card-label">
                                Filtreye Uyan Makine Sayısı (Yıl Sonu)
                            </div>
                            <div className="machine-stats-page__card-value">
                                {totalMachines}
                            </div>
                        </div>
                        <div className="machine-stats-page__card">
                            <div className="machine-stats-page__card-label">
                                Toplam Kiralama (Filtreli)
                            </div>
                            <div className="machine-stats-page__card-value">
                                {totalRentals}
                            </div>
                        </div>
                        <div className="machine-stats-page__card">
                            <div className="machine-stats-page__card-label">
                                Farklı Müşteri Sayısı (Bu Yıl)
                            </div>
                            <div className="machine-stats-page__card-value">
                                {uniqueCustomerCount}
                            </div>
                        </div>
                        <div className="machine-stats-page__card">
                            <div className="machine-stats-page__card-label">
                                Toplam Boş Ay (Makine x Ay)
                            </div>
                            <div className="machine-stats-page__card-value">
                                {totalEmptyMonthsApprox}
                            </div>
                        </div>
                    </div>

                    {/* Grafikler */}
                    <div className="machine-stats-page__charts">
                        {/* 1. Grafik: Başlangıç / Boşta */}
                        <div className="machine-stats-page__chart">
                            <h2 className="machine-stats-page__chart-title">
                                Aylara Göre Başlangıç / Boşta Makine
                            </h2>
                            <div className="machine-stats-page__bars">
                                {monthlyStarts.map((val, idx) => {
                                    const activeCount = monthlyActiveMachines[idx];
                                    const totalAvailableThisMonth = monthlyAvailableMachines[idx];
                                    const idleCount = totalAvailableThisMonth - activeCount;

                                    return (
                                        <div key={idx} className="machine-stats-page__bar-row">
                                            <div className="machine-stats-page__bar-label">{ayIsimleri[idx]}</div>
                                            <div className="machine-stats-page__bar-track">
                                                <div
                                                    className="machine-stats-page__bar-fill"
                                                    style={{ width: `${(val / Math.max(...monthlyStarts, 1)) * 100}%` }}
                                                />
                                            </div>
                                            <div className="machine-stats-page__bar-value">
                                                {val}
                                                <span className="machine-stats-page__bar-value--idle">
                                                    {" / "}
                                                    {idleCount > 0 ? idleCount : 0}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 2. Grafik: Bitiş */}
                        <div className="machine-stats-page__chart">
                            <h2 className="machine-stats-page__chart-title">
                                Aylara Göre Kiralama Bitiş Sayısı
                            </h2>
                            <div className="machine-stats-page__bars">
                                {monthlyEnds.map((val, idx) => (
                                    <div key={idx} className="machine-stats-page__bar-row">
                                        <div className="machine-stats-page__bar-label">{ayIsimleri[idx]}</div>
                                        <div className="machine-stats-page__bar-track">
                                            <div
                                                className="machine-stats-page__bar-fill machine-stats-page__bar-fill--secondary"
                                                style={{ width: `${(val / Math.max(...monthlyEnds, 1)) * 100}%` }}
                                            />
                                        </div>
                                        <div className="machine-stats-page__bar-value">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 3. Grafik: Çalışan */}
                        <div className="machine-stats-page__chart">
                            <h2 className="machine-stats-page__chart-title">
                                Aylara Göre Çalışan Makine Sayısı (Benzersiz)
                            </h2>
                            <div className="machine-stats-page__bars">
                                {monthlyActiveMachines.map((val, idx) => {
                                    const max = Math.max(...monthlyActiveMachines, 1);
                                    const width = (val / max) * 100;
                                    const activeCount = val;

                                    return (
                                        <div key={idx} className="machine-stats-page__bar-row">
                                            <div className="machine-stats-page__bar-label">{ayIsimleri[idx]}</div>
                                            <div className="machine-stats-page__bar-track">
                                                <div
                                                    className="machine-stats-page__bar-fill machine-stats-page__bar-fill--third"
                                                    style={{ width: `${width}%` }}
                                                />
                                            </div>
                                            <div className="machine-stats-page__bar-value">
                                                {activeCount}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Makine Bazlı Tablo */}
                    <div className="machine-stats-page__table-wrapper">
                        <div className="machine-stats-page__table-header-row">
                            <h2 className="machine-stats-page__section-title">
                                Makine Bazlı İstatistikler
                            </h2>
                        </div>
                        <div className="machine-stats-page__summary-line">
                            Filtrelere uyan <strong>{machineSummaries.length}</strong> makine için
                            yaklaşık <strong>{totalEmptyDays}</strong> gün
                            (~<strong>{totalEmptyMonthsApprox}</strong> ay)
                            boş kalma süresi hesaplandı.
                        </div>
                        {machineSummaries.length === 0 ? (
                            <div className="machine-stats-page__info">Seçilen filtreler için istatistik bulunamadı.</div>
                        ) : (
                            <table className="machine-stats-page__table">
                                <thead>
                                    <tr>
                                        <th>Makine (Seri / Kod)</th>
                                        <th>Bu Yıl Kiralama Adedi</th>
                                        <th>Bu Yıl Tahmini Çalışma Günü</th>
                                        <th>Bu Yıl Boş Gün Sayısı</th>
                                        <th>İlk Kiralama Tarihi</th>
                                        <th>Son Kiralama Bitiş Tarihi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {machineSummaries.map((m) => (
                                        <tr key={m.towerCraneId}>
                                            <td>{m.serialNo || `#${m.towerCraneId}`}</td>
                                            <td>{m.rentalCount}</td>
                                            <td>{m.totalDays}</td>
                                            <td>{m.emptyDays}</td>
                                            <td>{formatDate(m.firstStart)}</td>
                                            <td>{formatDate(m.lastEnd)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* MÜŞTERİ TABLOSU */}
                    <div className="machine-stats-page__table-wrapper">
                        <div className="machine-stats-page__table-header-row">
                            <h2 className="machine-stats-page__section-title">
                                Müşteri Bazlı İstatistikler
                            </h2>
                            <div className="machine-stats-page__toggle-group">
                                <button
                                    className={`machine-stats-page__toggle-btn ${customerTypeToggle === 'contractor' ? 'active' : ''}`}
                                    onClick={() => setCustomerTypeToggle('contractor')}
                                >
                                    Müteahhit (Kiralama Yapan)
                                </button>
                                <button
                                    className={`machine-stats-page__toggle-btn ${customerTypeToggle === 'investor' ? 'active' : ''}`}
                                    onClick={() => setCustomerTypeToggle('investor')}
                                >
                                    Yatırımcı (Makine Sahibi)
                                </button>
                            </div>
                        </div>

                        {filteredCustomerSummaries.length === 0 ? (
                            <div className="machine-stats-page__info">
                                Seçilen filtreler ve müşteri tipi için kiralama bilgisi bulunamadı.
                            </div>
                        ) : (
                            <table className="machine-stats-page__table">
                                <thead>
                                    <tr>
                                        <th>Müşteri Firma Adı</th>
                                        <th>Toplam Kiralama Sayısı</th>
                                        <th>Kiraladığı Farklı Makine Sayısı</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCustomerSummaries.map((c) => (
                                        <tr key={c.id}>
                                            <td>{c.name}</td>
                                            <td>{c.rentalCount}</td>
                                            <td>{c.uniqueMachineCount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default MachineStatisticsPage;